/*
  Archelia Predictive Search (Typesense)
  Sostituisce il motore nativo di Shopify per il menu a tendina.
*/

class ArcheliaPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.dropdown = this.querySelector('.arch-predictive-dropdown');
    
    this.TYPESENSE_URL = 'https://typesense-production-3d76.up.railway.app';
    this.TYPESENSE_API_KEY = '0usGelJob5PNVpsarJxhhOuIgQo5Mq86';
    this.COLLECTION = 'products';
    
    this.debounceTimer = null;
    this.recentSearches = this.getRecentSearches();
    
    this.setupEventListeners();
  }

  connectedCallback() {
    // Chiudi il menu se si clicca fuori
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.close();
      }
    });
  }

  setupEventListeners() {
    if (!this.input) return;

    this.input.addEventListener('focus', () => {
      this.handleInput(); // Mostra zero-state o risultati correnti
    });

    this.input.addEventListener('input', () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.handleInput(), 150);
    });

    this.form.addEventListener('submit', (e) => {
      // Salva la ricerca nei recenti prima della sottomissione standard (per la full page search)
      const val = this.input.value.trim();
      if (val) this.addRecentSearch(val);
    });
  }

  open() {
    if (this.dropdown) {
      this.dropdown.classList.add('arch-is-open');
      document.body.classList.add('arch-search-open');
    }
  }

  close() {
    if (this.dropdown) {
      this.dropdown.classList.remove('arch-is-open');
      document.body.classList.remove('arch-search-open');
    }
  }

  handleInput() {
    const query = this.input.value.trim();
    
    if (query.length < 2) {
      this.renderZeroState();
      return;
    }

    this.renderLoading();
    this.fetchTypesense(query);
  }

  async fetchTypesense(query) {
    try {
      const searchParams = new URLSearchParams({
        q: query,
        query_by: 'title,original_name,semantic_tags,brand,family,product_group,category,technical_desc,description',
        query_by_weights: '100,100,100,100,80,80,80,60,50',
        sort_by: '_text_match:desc',
        per_page: '8', // 8 prodotti per mostrare 3-4 righe
        page: '1',
        filter_by: 'stock:>0 && publishedOnWeb:true',
        facet_by: 'brand,category',
        max_facet_values: '4'
      });

      const res = await fetch(`${this.TYPESENSE_URL}/collections/${this.COLLECTION}/documents/search?${searchParams.toString()}`, {
        headers: { 'X-TYPESENSE-API-KEY': this.TYPESENSE_API_KEY }
      });
      
      if (!res.ok) throw new Error('Network response was not ok');
      const data = await res.json();
      
      this.renderResults(query, data);
    } catch (error) {
      console.error('Typesense Error:', error);
      // Fai il fallback sulla barra di ricerca nativa o non fare niente
    }
  }

  async fetchTrending() {
    // Chiamata wildcard * per prendere i trend
    try {
      const searchParams = new URLSearchParams({
        q: '*',
        per_page: '8',
        page: '1',
        filter_by: 'stock:>0 && publishedOnWeb:true'
      });

      const res = await fetch(`${this.TYPESENSE_URL}/collections/${this.COLLECTION}/documents/search?${searchParams.toString()}`, {
        headers: { 'X-TYPESENSE-API-KEY': this.TYPESENSE_API_KEY }
      });
      const data = await res.json();
      return data.hits || [];
    } catch(e) {
      return [];
    }
  }

  async renderZeroState() {
    this.open();
    let html = `<div class="arch-pred-split">`;
    
    // Sezione Sinistra: Recenti
    html += `<div class="arch-pred-terms">
      <div class="arch-pred-heading">Ricerche recenti</div>`;
    
    if (this.recentSearches.length > 0) {
      this.recentSearches.forEach(term => {
        html += `
          <div class="arch-pred-recent-item" data-action="fill" data-term="${term}">
            <svg class="arch-pred-recent-icon" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            <span class="arch-pred-recent-text">${term}</span>
            <span class="arch-pred-recent-remove" data-action="remove" data-term="${term}">&times;</span>
          </div>
        `;
      });
    } else {
      html += `<div style="padding: 10px 15px; color: #9CA3AF; font-size: 0.9rem;">Nessuna ricerca recente</div>`;
    }
    html += `</div>`;

    // Sezione Destra: Potrebbe Interessarti (Trending)
    html += `<div class="arch-pred-products">
      <div class="arch-pred-heading">Potrebbe interessarti</div>
      <div class="arch-pred-grid arch-pred-trending-grid">
        <div style="padding:10px; color:#9CA3AF;">Caricamento...</div>
      </div>
    </div>`;

    html += `</div>`;
    this.dropdown.innerHTML = html;
    this.attachListListeners();

    // Carica i trending asincronamente
    const trending = await this.fetchTrending();
    const grid = this.dropdown.querySelector('.arch-pred-trending-grid');
    if (grid) {
      if (trending.length > 0) {
        grid.innerHTML = trending.map(hit => this.buildProductCard(hit.document)).join('');
      } else {
        grid.innerHTML = `<div style="padding:10px; color:#9CA3AF;">Nessun trend trovato.</div>`;
      }
    }
  }

  renderLoading() {
    this.open();
    this.dropdown.innerHTML = `<div class="arch-pred-loading">Ricerca in corso...</div>`;
  }

  renderResults(query, data) {
    this.open();
    const hits = data.hits || [];
    
    if (hits.length === 0) {
      this.dropdown.innerHTML = `
        <div style="padding: 30px; text-align: center;">
          <div style="font-size: 1.1rem; font-weight: bold; color: #0F1111;">Nessun risultato trovato</div>
          <div style="color: #767676; margin-top: 5px;">Non abbiamo trovato prodotti per "${query}"</div>
        </div>
      `;
      return;
    }

    let html = `<div class="arch-pred-split">`;
    
    // Sinistra: Termini Suggeriti (prendiamo i primi 3 titoli dei risultati per simulare i termini o usiamo le facets)
    // Sull'app mobile i termini suggeriti sono generati dalle facets brand/category o dai titoli.
    let suggestions = [];
    if (data.facet_counts) {
      data.facet_counts.forEach(f => {
        if (f.counts.length > 0) {
          // Traduzione Facets tramite la Mappa Globale Liquid se esiste, altrimenti usa il valore
          let rawValue = f.counts[0].value;
          let label = (window.archeliaMap && window.archeliaMap[rawValue]) ? window.archeliaMap[rawValue] : rawValue;
          suggestions.push(`${query} ${label}`);
        }
      });
    }
    // Aggiungi un paio di suggerimenti basati sui primi risultati se non bastano
    for (let i=0; i<Math.min(2, hits.length); i++) {
      let words = hits[i].document.title.split(' ').slice(0, 3).join(' ');
      if (!suggestions.includes(words) && words.toLowerCase() !== query.toLowerCase()) {
        suggestions.push(words);
      }
    }

    html += `<div class="arch-pred-terms">
      <div class="arch-pred-heading">Suggerimenti</div>`;
    
    html += `
      <div class="arch-pred-term-item" data-action="submit">
        <span class="arch-pred-term-text">
          <svg width="18" height="18" fill="none" stroke="#9CA3AF" stroke-width="2" viewBox="0 0 24 24"><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          <strong style="color:#0F1111">${query}</strong>
        </span>
      </div>
    `;

    suggestions.slice(0, 4).forEach(s => {
      html += `
        <div class="arch-pred-term-item" data-action="fill" data-term="${s}">
          <span class="arch-pred-term-text">
            <svg width="18" height="18" fill="none" stroke="#9CA3AF" stroke-width="2" viewBox="0 0 24 24"><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            ${s.toLowerCase().replace(query.toLowerCase(), `<strong>${query}</strong>`)}
          </span>
          <span class="arch-pred-term-arrow" title="Completa">↗</span>
        </div>
      `;
    });
    html += `</div>`;

    // Destra: Prodotti
    html += `<div class="arch-pred-products">
      <div class="arch-pred-heading">Prodotti (${data.found})</div>
      <div class="arch-pred-grid">`;
    
    hits.forEach(hit => {
      html += this.buildProductCard(hit.document);
    });

    html += `</div>`;
    
    // Bottone "Vedi tutti"
    html += `
      <div style="margin-top: 15px; text-align: center;">
        <button type="submit" style="background: transparent; border: none; color: #008394; font-weight: bold; cursor: pointer; text-decoration: underline;">
          Vedi tutti i ${data.found} risultati
        </button>
      </div>
    `;

    html += `</div></div>`;
    
    this.dropdown.innerHTML = html;
    this.attachListListeners();
  }

  buildProductCard(doc) {
    const handle = doc.handle;
    const url = `/products/${handle}`;
    const imgUrl = doc.image_url || '';
    const originalPrice = doc.price ? parseFloat(doc.price) : 0;

    // Applica sconto promo se presente
    const promos = window.__archeliaPromos || [];
    let discountPct = 0;
    for (const p of promos) {
      if (p.handles && p.handles.includes(handle)) { discountPct = p.pct; break; }
      if (p.collection && doc.collections && doc.collections.includes(p.collection)) { discountPct = p.pct; break; }
    }

    let priceHtml = '';
    if (originalPrice > 0) {
      if (discountPct > 0) {
        const salePrice = originalPrice * (1 - discountPct / 100);
        priceHtml = `<span style="color:#CC0C39;font-weight:700;">€${salePrice.toFixed(2).replace('.', ',')}</span> <span style="text-decoration:line-through;color:#565959;font-size:0.85em;">€${originalPrice.toFixed(2).replace('.', ',')}</span>`;
      } else {
        priceHtml = '€' + originalPrice.toFixed(2).replace('.', ',');
      }
    }
    
    return `
      <a href="${url}" class="arch-pred-product-card">
        <div class="arch-pred-product-img">
          <img src="${imgUrl}" alt="">
        </div>
        <div class="arch-pred-product-info">
          <div class="arch-pred-product-title">${doc.title}</div>
          <div class="arch-pred-product-price">${priceHtml}</div>
        </div>
      </a>
    `;
  }

  attachListListeners() {
    const fillBtns = this.dropdown.querySelectorAll('[data-action="fill"]');
    fillBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        // Se si clicca la X per rimuovere, non fillare
        if (e.target.closest('[data-action="remove"]')) return;
        
        const term = btn.getAttribute('data-term');
        this.input.value = term;
        this.input.focus();
        this.handleInput(); // ri-scatena la ricerca
      });
    });

    const removeBtns = this.dropdown.querySelectorAll('[data-action="remove"]');
    removeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const term = btn.getAttribute('data-term');
        this.removeRecentSearch(term);
        this.handleInput(); // ri-renderizza zero-state
      });
    });

    const submitBtns = this.dropdown.querySelectorAll('[data-action="submit"]');
    submitBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        this.form.submit();
      });
    });
  }

  // LocalStorage per Ricerche Recenti
  getRecentSearches() {
    try {
      const data = localStorage.getItem('archelia_recent_searches');
      return data ? JSON.parse(data) : [];
    } catch(e) {
      return [];
    }
  }

  addRecentSearch(term) {
    let searches = this.getRecentSearches();
    searches = searches.filter(s => s.toLowerCase() !== term.toLowerCase());
    searches.unshift(term);
    if (searches.length > 5) searches = searches.slice(0, 5);
    localStorage.setItem('archelia_recent_searches', JSON.stringify(searches));
    this.recentSearches = searches;
  }

  removeRecentSearch(term) {
    let searches = this.getRecentSearches();
    searches = searches.filter(s => s.toLowerCase() !== term.toLowerCase());
    localStorage.setItem('archelia_recent_searches', JSON.stringify(searches));
    this.recentSearches = searches;
  }
}

customElements.define('archelia-predictive-search', ArcheliaPredictiveSearch);
